<template>
  <div class="pl-page">
    <!-- ────────── HEADER ────────── -->
    <header class="pl-header">
      <div class="pl-header__inner">
        <div class="pl-header__title-wrap">
          <h1 class="pl-title">חבילות</h1>
          <span class="pl-total-pill" :title="`סה״כ ${plans.length} חבילות`">
            <v-icon size="14">mdi-package-variant-closed</v-icon>
            <span>{{ plans.length.toLocaleString('he-IL') }}</span>
          </span>
        </div>
        <button type="button" class="pl-cta" @click="openCreateDIalog">
          <v-icon size="17">mdi-plus</v-icon>
          <span>הוסף חבילה</span>
        </button>
      </div>
    </header>

    <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

    <!-- ────────── MAIN ────────── -->
    <main class="pl-main">

      <!-- CARRIER BREAKDOWN STATS -->
      <section class="pl-stats" aria-label="פילוח לפי מפעיל">
        <div class="pl-stat pl-stat--all">
          <div class="pl-stat__icon"><v-icon size="19">mdi-package-variant</v-icon></div>
          <div class="pl-stat__body">
            <span class="pl-stat__label">סה״כ חבילות</span>
            <strong class="pl-stat__value">{{ plans.length.toLocaleString('he-IL') }}</strong>
          </div>
        </div>
        <div class="pl-stat pl-stat--hot">
          <div class="pl-stat__icon"><v-icon size="19">mdi-cellphone-wireless</v-icon></div>
          <div class="pl-stat__body">
            <span class="pl-stat__label">הוט מובייל</span>
            <strong class="pl-stat__value">{{ countByCarrier('hotMobile').toLocaleString('he-IL') }}</strong>
          </div>
        </div>
        <div class="pl-stat pl-stat--partner">
          <div class="pl-stat__icon"><v-icon size="19">mdi-cellphone-wireless</v-icon></div>
          <div class="pl-stat__body">
            <span class="pl-stat__label">פרטנר</span>
            <strong class="pl-stat__value">{{ countByCarrier('partner').toLocaleString('he-IL') }}</strong>
          </div>
        </div>
        <div class="pl-stat pl-stat--click">
          <div class="pl-stat__icon"><v-icon size="19">mdi-cellphone-wireless</v-icon></div>
          <div class="pl-stat__body">
            <span class="pl-stat__label">קליקול</span>
            <strong class="pl-stat__value">{{ countByCarrier('clickCallMobile').toLocaleString('he-IL') }}</strong>
          </div>
        </div>
      </section>

      <!-- PLANS GRID -->
      <div class="pl-grid">

        <article v-for="(item, index) in plans" :key="item._id || index" class="pl-card"
          :class="[`pl-card--${item.operating_company}`, { 'pl-card--hidden': !item.show_to_user }]">

          <!-- carrier-colored top strip -->
          <div class="pl-card__strip" aria-hidden="true"></div>

          <!-- visibility chip: only as an exception flag when the plan is HIDDEN -->
          <div v-if="!item.show_to_user" class="pl-card__visibility">
            <v-icon size="11">mdi-eye-off-outline</v-icon>
            <span>מוסתר</span>
          </div>

          <!-- carrier logo -->
          <div class="pl-card__logo-wrap">
            <v-avatar :size="72" class="pl-card__logo">
              <img :src="getCompanyLogo(item.operating_company)" alt="logo">
            </v-avatar>
          </div>

          <!-- plan content -->
          <div class="pl-card__body">
            <h3 class="pl-card__name">{{ item.name }}</h3>
            <p class="pl-card__desc">{{ item.details }}</p>
          </div>

          <!-- price -->
          <div class="pl-card__price">
            <span class="pl-card__price-amount">{{ item.price }}</span>
            <span class="pl-card__price-suffix">₪</span>
          </div>

          <!-- actions -->
          <div class="pl-card__actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <button v-on="on" type="button" class="pl-action pl-action--edit" @click="openEditDialog(item)"
                  aria-label="עריכה">
                  <v-icon size="17">mdi-pencil-outline</v-icon>
                </button>
              </template>
              <span>עריכת חבילה</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <button v-on="on" type="button" class="pl-action pl-action--delete" @click="deletePlan(item)"
                  aria-label="מחיקה">
                  <v-icon size="17">mdi-delete-outline</v-icon>
                </button>
              </template>
              <span>מחיקת חבילה</span>
            </v-tooltip>
          </div>

        </article>

        <!-- ADD-PLAN PLACEHOLDER CARD -->
        <button v-if="plans.length > 0" type="button" class="pl-add-card" @click="openCreateDIalog">
          <div class="pl-add-card__icon">
            <v-icon size="32">mdi-plus</v-icon>
          </div>
          <span class="pl-add-card__text">הוסף חבילה חדשה</span>
        </button>

        <!-- EMPTY STATE -->
        <div v-if="plans.length === 0 && !progressShow" class="pl-empty">
          <div class="pl-empty__icon">
            <v-icon size="42">mdi-package-variant</v-icon>
          </div>
          <h3 class="pl-empty__title">אין חבילות להצגה</h3>
          <p class="pl-empty__hint">צור את החבילה הראשונה כדי להתחיל</p>
          <button type="button" class="pl-empty__btn" @click="openCreateDIalog">
            <v-icon size="17">mdi-plus</v-icon>
            <span>הוסף חבילה חדשה</span>
          </button>
        </div>

      </div>
    </main>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64" color="#0d2c6d"></v-progress-circular>
    </v-overlay>

    <PlansAddEditDialog v-model="editPlanDialog" v-if="editPlanDialog" :planToEdit="planToEdit" :formTitle="formTitle"
      @planEdited="planEditedSaved" @planCreated="newPlanSaved" />

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </div>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import Utils from "@/util/utils";
import PlansAddEditDialog from '@/components/admin/plans/dialogs/PlansAddEditDialog'
import ProgressShow from '@/components/widgets/progressShow.vue'

export default {
  components: {
    SnackBar,
    PlansAddEditDialog,
    ProgressShow
  },
  data: () => ({
    overlay: false,
    editPlanDialog: false,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    progressShow: true,
    plans: [],
    planToEdit: {},
    formTitle: ""
  }),
  methods: {
    async getPlans() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/plans/get_all_plans`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();
        this.progressShow = false;
        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          this.plans = jsonObject;
        }
      } catch (error) {
        this.progressShow = false;
        this.showSnackBar("Error get files: " + error, "red");
      }
    },
    openEditDialog(item) {
      this.planToEdit = item;
      this.formTitle = "ערוך חבילה"
      this.editPlanDialog = true;
    },
    openCreateDIalog() {
      this.formTitle = "הוסף חבילה"
      this.planToEdit = {}
      this.editPlanDialog = true
    },
    closeDelete() {
      this.openMode = false;
    },
    planEditedSaved(item, text, color) {
      this.showSnackBar(text, color)
      if (item === "") {
        return
      }
      const index = this.plans.findIndex(plan => plan._id === item._id);
      let g = Object.assign(this.plans[index], item);
    },
    newPlanSaved(item, text, color) {
      this.showSnackBar(text, color)
      if (item === "") {
        return
      }
      this.plans.push(item);
    },
    async deletePlan(item) {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        const planJSON = JSON.stringify({ plan: item });
        let api = process.env.VUE_APP_BASE_URL + `/plans/delete_plan`;
        const res = await fetch(api, ApiServices.requestOptions("DELETE", planJSON, token));
        const jsonObject = await res.json();
        this.progressShow = false;
        if (res.status >= 400) {
          this.showSnackBar("שגיאה במחיקת החבילה: " + jsonObject.message, "red");
        } else if (res.status === 200) {
          this.showSnackBar("החבילה נמחקה בהצלחה!", "green");
          // Use splice to remove the item at the specified index
          const index = this.plans.findIndex(plan => plan._id === item._id);
          this.plans.splice(index, 1);
        }
      } catch (error) {
        this.showSnackBar("Error get files: " + error, "red");
      }
    },
    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },
    getCompanyLogo(company) {
      const extension = company === 'clickCallMobile' ? 'svg' : 'png';
      return require(`@/assets/${company}.${extension}`);
    },
    // ── Presentation helper for stats ──
    countByCarrier(carrier) {
      return this.plans.filter(p => p.operating_company === carrier).length;
    }
  },
  created() {
    this.getPlans();
  },
}
</script>

<style scoped>
/* ─────────────────────── TOKENS ─────────────────────── */
.pl-page {
  --p-navy: #0d2c6d;
  --p-navy-soft: #142f78;
  --p-indigo: #3F51B5;
  --p-indigo-deep: #303f9f;
  --p-indigo-soft: rgba(63, 81, 181, 0.10);
  --p-purple: #8066f0;
  --p-gold: #fdb721;
  --p-gold-deep: #e09e0a;

  /* Carrier colors */
  --p-hot: #e30613;
  --p-hot-soft: rgba(227, 6, 19, 0.08);
  --p-partner: #14a89d;
  --p-partner-soft: rgba(20, 168, 157, 0.10);
  --p-click: #f5a623;
  --p-click-soft: rgba(245, 166, 35, 0.12);

  --p-bg: #f4f5f9;
  /* slightly indigo-tinted bg */
  --p-surface: #ffffff;
  --p-border: #e3e8ef;
  --p-border-soft: #eef2f7;
  --p-text: #0f172a;
  --p-text-soft: #475569;
  --p-text-mute: #94a3b8;
  --p-red: #c74747;

  --p-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
  --p-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
  --p-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
  --p-ease: cubic-bezier(0.22, 1, 0.36, 1);

  min-height: 100vh;
  padding-bottom: 60px;
  background: var(--p-bg);
  direction: rtl;
  font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────────── HEADER ─────────────────────── */
/* Note: indigo flourish — visual marker for the Plans page */
.pl-header {
  background:
    radial-gradient(800px 200px at 90% -30%, rgba(63, 81, 181, 0.30), transparent 60%),
    linear-gradient(135deg, #0d2c6d 0%, #1c2a85 55%, #283b9d 100%);
  color: #fff;
  padding: 12px 0 14px;
  box-shadow: var(--p-shadow-md);
}

.pl-header__inner {
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pl-header__title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.pl-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.pl-total-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.20);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  font-feature-settings: "tnum" 1;
}

.pl-total-pill ::v-deep .v-icon {
  color: rgba(255, 255, 255, 0.85) !important;
}

/* CTA in header */
.pl-cta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.30);
  background: rgba(63, 81, 181, 0.55);
  color: #fff;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.22s var(--p-ease);
  backdrop-filter: blur(6px);
  flex-shrink: 0;
}

.pl-cta:hover {
  background: var(--p-indigo);
  border-color: rgba(255, 255, 255, 0.45);
  transform: translateY(-1px);
  box-shadow: 0 6px 14px -4px rgba(63, 81, 181, 0.55);
}

.pl-cta ::v-deep .v-icon {
  color: #fff !important;
}

/* ─────────────────────── MAIN + STATS ─────────────────────── */
.pl-main {
  max-width: 1500px;
  margin: 22px auto 0;
  padding: 0 22px;
}

.pl-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.pl-stat {
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 70px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--p-surface);
  border: 1px solid var(--p-border-soft);
  box-shadow: var(--p-shadow-md);
  animation: pl-cardIn 0.45s var(--p-ease) backwards;
}

.pl-stat:nth-child(1) {
  animation-delay: 0.00s;
}

.pl-stat:nth-child(2) {
  animation-delay: 0.06s;
}

.pl-stat:nth-child(3) {
  animation-delay: 0.12s;
}

.pl-stat:nth-child(4) {
  animation-delay: 0.18s;
}

@keyframes pl-cardIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pl-stat__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 11px;
}

.pl-stat__body {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.pl-stat__label {
  color: var(--p-text-mute);
  font-size: 0.76rem;
  font-weight: 700;
}

.pl-stat__value {
  color: var(--p-text);
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.01em;
  font-feature-settings: "tnum" 1;
}

.pl-stat--all .pl-stat__icon {
  color: var(--p-indigo);
  background: var(--p-indigo-soft);
}

.pl-stat--hot .pl-stat__icon {
  color: var(--p-hot);
  background: var(--p-hot-soft);
}

.pl-stat--partner .pl-stat__icon {
  color: var(--p-partner);
  background: var(--p-partner-soft);
}

.pl-stat--click .pl-stat__icon {
  color: var(--p-click);
  background: var(--p-click-soft);
}

/* ─────────────────────── PLAN CARD GRID ─────────────────────── */
.pl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 18px;
}

.pl-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--p-surface);
  border: 1px solid var(--p-border-soft);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s var(--p-ease), box-shadow 0.3s var(--p-ease), border-color 0.3s var(--p-ease);
  animation: pl-cardIn 0.5s var(--p-ease) backwards;
  box-shadow: var(--p-shadow-sm);
}

.pl-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--p-shadow-lg);
  border-color: rgba(13, 44, 109, 0.14);
}

/* carrier-colored top strip — the visual anchor */
.pl-card__strip {
  height: 4px;
  width: 100%;
}

.pl-card--hotMobile .pl-card__strip {
  background: linear-gradient(90deg, var(--p-hot), #b00510);
}

.pl-card--partner .pl-card__strip {
  background: linear-gradient(90deg, var(--p-partner), #0e7a72);
}

.pl-card--clickCallMobile .pl-card__strip {
  background: linear-gradient(90deg, var(--p-click), #d18514);
}

/* subtle gradient flourish in card bg per carrier */
.pl-card--hotMobile {
  background: linear-gradient(180deg, var(--p-hot-soft) 0%, transparent 30%, var(--p-surface) 70%);
}

.pl-card--partner {
  background: linear-gradient(180deg, var(--p-partner-soft) 0%, transparent 30%, var(--p-surface) 70%);
}

.pl-card--clickCallMobile {
  background: linear-gradient(180deg, var(--p-click-soft) 0%, transparent 30%, var(--p-surface) 70%);
}

/* visibility chip (top-left in RTL) */
.pl-card__visibility {
  position: absolute;
  top: 14px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  z-index: 2;
  color: var(--p-text-mute);
  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(148, 163, 184, 0.28);
  backdrop-filter: blur(4px);
}

.pl-card__visibility ::v-deep .v-icon {
  color: inherit !important;
}



/* hidden-state card treatment */
.pl-card--hidden {
  opacity: 0.72;
}

.pl-card--hidden .pl-card__logo img {
  filter: grayscale(60%);
}

.pl-card--hidden .pl-card__price-amount {
  color: var(--p-text-mute);
}

/* logo */
.pl-card__logo-wrap {
  padding: 28px 16px 20px;
  display: flex;
  justify-content: center;
}

.pl-card__logo {
  box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.18);
  transition: transform 0.3s var(--p-ease);
}

.pl-card:hover .pl-card__logo {
  transform: scale(1.05);
}


/* content */
.pl-card__body {
  padding: 6px 18px 14px;
  flex: 1;
  text-align: center;
}

.pl-card__name {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--p-navy);
  letter-spacing: -0.01em;
}

.pl-card__desc {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--p-text-soft);
  min-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* price — gold treatment, hero element */
.pl-card__price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  padding: 8px 14px 4px;
}

.pl-card__price-amount {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: var(--p-gold-deep);
  line-height: 1;
  font-feature-settings: "tnum" 1;
  text-shadow: 0 1px 2px rgba(253, 183, 33, 0.15);
}

.pl-card__price-suffix {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-gold-deep);
  opacity: 0.85;
}



/* actions — symmetric icon-only buttons, no heavy footer */
.pl-card__actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 6px 16px 16px;
}

.pl-action {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: 1px solid var(--p-border);
  border-radius: 9px;
  background: var(--p-surface);
  color: var(--p-text-soft);
  cursor: pointer;
  transition: all 0.2s var(--p-ease);
}

.pl-action ::v-deep .v-icon {
  color: inherit !important;
}

.pl-action:hover {
  border-color: transparent;
  transform: translateY(-1px);
  color: #fff;
}

.pl-action--edit:hover {
  background: var(--p-indigo);
  box-shadow: 0 5px 12px -4px rgba(63, 81, 181, 0.50);
}

.pl-action--delete:hover {
  background: var(--p-red);
  box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.50);
}

/* ─────────────────────── ADD-CARD PLACEHOLDER ─────────────────────── */
.pl-add-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  min-height: 320px;
  border: 2px dashed #cfd5e3;
  border-radius: 16px;
  background: transparent;
  color: var(--p-text-mute);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.25s var(--p-ease);
}

.pl-add-card:hover {
  border-color: var(--p-indigo);
  background: var(--p-indigo-soft);
  color: var(--p-indigo);
  transform: translateY(-3px);
}

.pl-add-card__icon {
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--p-indigo-soft);
  color: var(--p-indigo);
  transition: transform 0.3s var(--p-ease);
}

.pl-add-card:hover .pl-add-card__icon {
  background: var(--p-indigo);
  color: #fff;
  transform: rotate(90deg) scale(1.08);
}

.pl-add-card ::v-deep .v-icon {
  color: inherit !important;
}

.pl-add-card__text {
  font-size: 0.95rem;
  font-weight: 700;
}

/* ─────────────────────── EMPTY STATE ─────────────────────── */
.pl-empty {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  background: var(--p-surface);
  border: 1px dashed #cfd5e3;
  border-radius: 16px;
  text-align: center;
}

.pl-empty__icon {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--p-indigo);
  background: var(--p-indigo-soft);
}

.pl-empty__title {
  margin: 0 0 4px;
  color: var(--p-text);
  font-size: 1.1rem;
  font-weight: 700;
}

.pl-empty__hint {
  margin: 0 0 20px;
  color: var(--p-text-mute);
  font-size: 0.9rem;
}

.pl-empty__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--p-indigo) 0%, var(--p-indigo-deep) 100%);
  color: #fff;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  box-shadow: 0 8px 16px -6px rgba(63, 81, 181, 0.5);
  transition: all 0.2s var(--p-ease);
}

.pl-empty__btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.08);
}

.pl-empty__btn ::v-deep .v-icon {
  color: #fff !important;
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 900px) {
  .pl-header__inner {
    padding: 0 18px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .pl-title {
    font-size: 1.1rem;
  }

  .pl-stats {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .pl-main {
    padding: 0 12px;
    margin-top: 16px;
  }

  .pl-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 14px;
  }
}

@media (max-width: 480px) {
  .pl-cta span {
    display: none;
  }

  .pl-cta {
    padding: 7px 9px;
  }

  .pl-grid {
    grid-template-columns: 1fr;
  }

  .pl-card__price-amount {
    font-size: 1.7rem;
  }
}

@media (prefers-reduced-motion: reduce) {

  .pl-stat,
  .pl-card {
    animation: none;
  }
}
</style>